// Manages pre-opened windows so we can reuse them when rendering detached rooms

import { isPopupDebugEnabled } from './PopupDebug';
import { reportDetachedIssue } from './DetachedIssueLogger';

type DetachedWindowManagerState = {
    reservations: { [roomId: string]: Window };
    activeWindows: Map<string, Window>;
    genericReservations: Window[];
    transferringWindows: Set<Window>;
    reservationTimeouts: { [roomId: string]: number };
};

function getDetachedWindowManagerState(): DetachedWindowManagerState {
    try {
        const w = window as any;
        if (!w.__HS_DETACHED_WINDOW_MANAGER) {
            w.__HS_DETACHED_WINDOW_MANAGER = {
                reservations: {},
                activeWindows: new Map<string, Window>(),
                genericReservations: [],
                transferringWindows: new Set<Window>(),
                reservationTimeouts: {},
            } satisfies DetachedWindowManagerState;
        }
        return w.__HS_DETACHED_WINDOW_MANAGER as DetachedWindowManagerState;
    } catch {
        return {
            reservations: {},
            activeWindows: new Map<string, Window>(),
            genericReservations: [],
            transferringWindows: new Set<Window>(),
            reservationTimeouts: {},
        };
    }
}

const {
    reservations,
    activeWindows,
    genericReservations,
    transferringWindows,
    reservationTimeouts,
} = getDetachedWindowManagerState();

const RESERVATION_TTL_MS = 30000;

const DEFAULT_FEATURES = 'width=1024,height=640,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no';

function debug(message: string, data?: Record<string, unknown>) {
    if (!isPopupDebugEnabled()) return;
    if (data) {
        console.debug(message, data);
    } else {
        console.debug(message);
    }
}

function warn(message: string, data?: Record<string, unknown>) {
    if (!isPopupDebugEnabled()) return;
    if (data) {
        console.warn(message, data);
    } else {
        console.warn(message);
    }
}

function getUserActivationSnapshot(sourceWindow?: Window | null): Record<string, unknown> | undefined {
    try {
        const nav = (sourceWindow?.navigator || navigator) as any;
        const activation = nav?.userActivation;
        if (!activation) return undefined;
        return {
            isActive: Boolean(activation.isActive),
            hasBeenActive: Boolean(activation.hasBeenActive),
        };
    } catch {
        return undefined;
    }
}

function clearReservationTimeout(roomId: string): void {
    const timeoutId = reservationTimeouts[roomId];
    if (timeoutId) {
        clearTimeout(timeoutId);
        delete reservationTimeouts[roomId];
    }
}

export function hasReservedWindow(roomId?: string): boolean {
    if (roomId) {
        const w = reservations[roomId];
        if (w) {
            if (!w.closed) {
                return true;
            }
            delete reservations[roomId];
            clearReservationTimeout(roomId);
        }
    }

    for (let i = genericReservations.length - 1; i >= 0; i -= 1) {
        const w = genericReservations[i];
        if (!w || w.closed) {
            genericReservations.splice(i, 1);
        }
    }

    return genericReservations.length > 0;
}

export function reserveDetachedWindow(
    roomId: string,
    features: string | undefined = undefined,
    sourceWindow?: Window | null
): Window | null {
    clearReservationTimeout(roomId);
    try {
        // IMPORTANT: Tournament lobby is rendered in a detached window via a React portal.
        // Click handlers still run in the main window JS context, so `window.open(...)` from the main window
        // does NOT have user activation when the user clicked inside the detached lobby.
        // If a caller passes `sourceWindow`, we call `sourceWindow.open(...)` so the browser sees user activation.
        const opener = sourceWindow || window;
        const w = opener.open('', '', features ?? DEFAULT_FEATURES);
        if (w) {
            // If the click happened inside a detached lobby popup we may open the reserved window
            // via that popup to preserve user-activation, but we still want the main client window
            // to be the `opener` so later focus calls from RoomBar (main window) work reliably.
            if (sourceWindow && sourceWindow !== window) {
                try {
                    (w as any).opener = window;
                } catch {
                    // ignore
                }
            }
            reservations[roomId] = w;
            reservationTimeouts[roomId] = window.setTimeout(() => {
                const current = reservations[roomId];
                if (current && current === w) {
                    warn('[detached] Reserved window expired (closing)', { roomId });
                    try {
                        if (!current.closed) {
                            current.close();
                        }
                    } catch {
                        // Ignore close errors
                    }
                    delete reservations[roomId];
                }
                delete reservationTimeouts[roomId];
            }, RESERVATION_TTL_MS);
            debug('[detached] Reserved window created', {
                roomId,
                userActivation: getUserActivationSnapshot(opener),
                mainUserActivation: opener === window ? undefined : getUserActivationSnapshot(window),
            });
            return w;
        }
        reportDetachedIssue('reserve_failed', {
            roomId,
            userActivation: getUserActivationSnapshot(opener),
            mainUserActivation: opener === window ? undefined : getUserActivationSnapshot(window),
        });
        warn('[detached] Failed to reserve window (popup blocked?)', {
            roomId,
            userActivation: getUserActivationSnapshot(opener),
            mainUserActivation: opener === window ? undefined : getUserActivationSnapshot(window),
        });
    } catch (e) {
        // Ignore (popup may be blocked)
        const opener = sourceWindow || window;
        reportDetachedIssue('reserve_failed', {
            roomId,
            userActivation: getUserActivationSnapshot(opener),
            mainUserActivation: opener === window ? undefined : getUserActivationSnapshot(window),
        });
        warn('[detached] Failed to reserve window (exception)', {
            roomId,
            userActivation: getUserActivationSnapshot(opener),
            mainUserActivation: opener === window ? undefined : getUserActivationSnapshot(window),
        });
    }
    return null;
}

export function releaseReservedWindow(roomId: string): void {
    clearReservationTimeout(roomId);
    const w = reservations[roomId];
    if (w) {
        debug('[detached] Releasing reserved window', { roomId });
        try {
            if (!w.closed) {
                w.close();
            }
        } catch {
            // Ignore close errors
        }
        delete reservations[roomId];
    }
}

export function consumeDetachedWindow(roomId: string): Window | null {
    const w = reservations[roomId];
    if (w) {
        clearReservationTimeout(roomId);
        delete reservations[roomId];
        activeWindows.set(roomId, w); // Track as active
        debug('[detached] Consumed reserved window', { roomId });
        return w;
    }
    return null;
}

// Track a detached window that was opened without a pre-reservation (e.g. server-initiated joins).
// This enables table-move transfers to find the existing window via activeWindows.
export function registerActiveWindow(roomId: string, w: Window): void {
    if (!roomId) return;
    if (!w || (w as any).closed) return;
    activeWindows.set(roomId, w);
}

// Transfer a window from one roomId to another
// Used when moving between tournament tables to reuse the same detached window
export function transferDetachedWindow(fromRoomId: string, toRoomId: string): Window | null {
    // Check in reservations first (pre-opened windows), then in active windows (already consumed)
    const w = reservations[fromRoomId] || activeWindows.get(fromRoomId);
    
    if (w && !w.closed) {
        clearReservationTimeout(fromRoomId);
        clearReservationTimeout(toRoomId);
        debug('[detached] Transferring window', { fromRoomId, toRoomId });

        // If the destination already has a pre-opened reserved window, close it to avoid orphaned blank popups.
        const existingTo = reservations[toRoomId];
        if (existingTo && existingTo !== w) {
            try {
                if (!existingTo.closed) {
                    existingTo.close();
                }
            } catch {
                // Ignore close errors
            }
            delete reservations[toRoomId];
        }
        // Remove from old tracking
        delete reservations[fromRoomId];
        activeWindows.delete(fromRoomId);
        
        // Add to new tracking
        reservations[toRoomId] = w;
        activeWindows.set(toRoomId, w);
        
        // Mark as transferring to prevent WindowPortal from closing it during unmount
        transferringWindows.add(w);
        return w;
    }
    return null;
}

// Check if a window is currently being transferred
export function isWindowTransferring(w: Window): boolean {
    return transferringWindows.has(w);
}

// Clear the transferring flag after the new WindowPortal successfully mounts
export function clearWindowTransferring(w: Window): void {
    transferringWindows.delete(w);
}

export function reserveGenericWindow(features: string = DEFAULT_FEATURES): Window | null {
    try {
        const w = window.open('', '', features);
        if (w) {
            genericReservations.push(w);
            return w;
        }
    } catch (e) {
        // Ignore
    }
    return null;
}

export function consumeAnyReservedWindow(): Window | null {
    // Prefer most recent
    while (genericReservations.length > 0) {
        const w = genericReservations.pop() as Window;
        if (w && !w.closed) return w;
    }
    return null;
}

// Clean up active window tracking when window is actually closed
export function unregisterActiveWindow(roomId: string): void {
    activeWindows.delete(roomId);
}

export function getActiveDetachedWindow(roomId: string): Window | null {
    const w = activeWindows.get(roomId);
    if (w) {
        if (!w.closed) return w;
        activeWindows.delete(roomId);
    }
    return null;
}

export function getActiveDetachedWindows(): Array<{ id: string; window: Window }> {
    const openWindows: Array<{ id: string; window: Window }> = [];

    activeWindows.forEach((w, id) => {
        if (!w || w.closed) {
            activeWindows.delete(id);
            return;
        }

        openWindows.push({ id, window: w });
    });

    return openWindows;
}
